// Library imports
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
// Component imports
import TabNav from '../../../../components/TabNav';
// Util imports
import { getGameTeamData } from '../../utils';
import { shortName } from '../../../../utils/strings';
import { formatOdd, formatOddButtonJuice, getSpreadJuice } from '../../../../utils/odds';
import { imageLoadError } from '../../../../utils';
// Styled components
import {
    InnerContent,
    CardTitle,
    TeamName,
    HighLightedField,
    LineCard,
    LineStatsGrid,
    LineStat,
    LineStatLabel,
    BookList,
    BookItem,
    BookInfo,
    BookLogo,
    BookLabel,
    BookOdds,
    OddsValue,
    BestWorstBadge,
    JuiceText,
} from './styles';
import { NoDataMessage, TeamLogoSmall } from '../styles';

const LineData = ({ game, sport, demo }) => {
    const { teams } = useSelector(state => state.app);
    const { awayTeamData, homeTeamData } = getGameTeamData(teams, game, demo, sport);
    const [type, setType] = useState(sport === 'mlb' || sport === 'nhl' ? 'moneyline' : 'spread');

    const bookType = type === 'moneyline' ? 'moneylines' : 'spreads';

    const calcAverageMoneyline = team => {
        if (!team.odds) return null;
        const teamData = team.teamId === game.awayTeam.teamId ? awayTeamData : homeTeamData;
        if (!teamData?.averageMoneylines) return null;
        if (team.odds.fullGame?.moneyline > 0) {
            return formatOdd(teamData.averageMoneylines?.dog?.avgML?.toFixed(0), 'moneyline');
        } else {
            return formatOdd(teamData.averageMoneylines?.favorite?.avgML?.toFixed(0), 'moneyline');
        }
    };

    const calcOddMovementVariant = team => {
        if (type === 'moneyline') {
            return team.odds?.fullGame?.moneyline > team.openMoneyLine ? 'success'
                : team.odds?.fullGame?.moneyline < team.openMoneyLine ? 'danger' : 'light';
        } else {
            const openingSpread = team.odds?.fullGame?.spread < 0 ? game.openingSpread * -1 : game.openingSpread;
            return team.odds?.fullGame?.spread > openingSpread ? 'success'
                : team.odds?.fullGame?.spread < openingSpread ? 'danger' : 'light';
        }
    };

    const renderBooks = (team) => {
        const lineType = team.teamId === game.awayTeam.teamId ? 'awayLine' : 'homeLine';
        const source = game.bookData?.[bookType];
        if (!Array.isArray(source)) return null;
        const rows = source
            .filter(b => !isNaN(b[lineType]))
            .sort((a, b) => Number(b[lineType]) - Number(a[lineType]));
        if (!rows.length) return null;

        const values = rows.map(b => b[lineType]);
        const allSame = values.every(v => v === values[0]);
        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);

        return (
            <BookList>
                {rows.map((book, i) => {
                    const val = book[lineType];
                    let isBest = false, isWorst = false;
                    if (!allSame) {
                        if (val === maxVal) isBest = true;
                        if (val === minVal) isWorst = true;
                    }
                    return (
                        <BookItem key={`${book.label}-${i}`}>
                            <BookInfo>
                                {book.logo && <BookLogo src={book.logo} alt={book.label} $bookColor={book.color} />}
                                <BookLabel>{book.label}</BookLabel>
                            </BookInfo>
                            <BookOdds>
                                <OddsValue $isBest={isBest} $isWorst={isWorst}>{formatOdd(val, type)}</OddsValue>
                                {(isBest || isWorst) && <BestWorstBadge $type={isBest ? 'best' : 'worst'}>{isBest ? 'Best' : 'Worst'}</BestWorstBadge>}
                            </BookOdds>
                        </BookItem>
                    );
                })}
            </BookList>
        );
    };

    const createTeamCard = team => {
        const hasMoneylineData = team.odds?.fullGame?.moneyline != null || team.openMoneyLine != null;
        const hasSpreadData = team.odds?.fullGame?.spread != null;
        if (!hasMoneylineData && !hasSpreadData) return null;
        const avgMoneyline = calcAverageMoneyline(team);

        return (
            <LineCard>
                <CardTitle title={team.teamName}>
                    {team.logoUrl && <TeamLogoSmall src={team.logoUrl} alt="" onError={imageLoadError} />}
                    <TeamName>{shortName(team)}</TeamName>
                </CardTitle>
                <LineStatsGrid $columns={type === 'moneyline' && avgMoneyline != null ? 3 : 2}>
                    <LineStat>
                        <LineStatLabel>Open</LineStatLabel>
                        <HighLightedField variant="light">
                            {type === 'moneyline'
                                ? formatOdd(team.openMoneyLine, 'moneyline')
                                : formatOdd(team.odds?.fullGame?.spread < 0 ? game.openingSpread * -1 : game.openingSpread)}
                        </HighLightedField>
                    </LineStat>
                    <LineStat>
                        <LineStatLabel>Now</LineStatLabel>
                        <HighLightedField variant={calcOddMovementVariant(team)}>
                            {type === 'moneyline' ? (
                                formatOdd(team.odds?.fullGame?.moneyline, 'moneyline')
                            ) : (
                                <>
                                    {formatOdd(team.odds?.fullGame?.spread)}
                                    <JuiceText>({formatOddButtonJuice(getSpreadJuice(team.odds?.fullGame?.spreadJuice), 'spread')})</JuiceText>
                                </>
                            )}
                        </HighLightedField>
                    </LineStat>
                    {type === 'moneyline' && avgMoneyline != null && (
                        <LineStat>
                            <LineStatLabel>Avg.</LineStatLabel>
                            <HighLightedField variant="light">{avgMoneyline}</HighLightedField>
                        </LineStat>
                    )}
                </LineStatsGrid>
                {renderBooks(team)}
            </LineCard>
        );
    };

    const away = game.awayTeam;
    const home = game.homeTeam;

    const hasLineData = away.odds?.fullGame?.moneyline != null || away.odds?.fullGame?.spread != null
        || home.odds?.fullGame?.moneyline != null || home.odds?.fullGame?.spread != null;

    return (
        <InnerContent fluid>
            <TabNav
                tabs={['moneyline', 'spread']}
                activeTab={type}
                setTab={setType}
                containerStyle={{ marginBottom: '0.75rem', width: '100%' }}
                buttonStyle={{ flex: 1 }}
                textStyle={{ width: '100%', textAlign: 'center', fontSize: '0.72rem', fontWeight: 800, padding: '0.55rem 0.65rem' }}
            />

            {!hasLineData ? (
                <NoDataMessage>
                    <i className="fas fa-chart-line" />
                    <p>No line data available. Check back closer to game time.</p>
                </NoDataMessage>
            ) : (
                <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 160px', minWidth: 0 }}>{createTeamCard(away)}</div>
                    <div style={{ flex: '1 1 160px', minWidth: 0 }}>{createTeamCard(home)}</div>
                </div>
            )}
        </InnerContent>
    );
};

export default LineData;
